import React from 'react';

const BusinessOverview: React.FC = () => {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          {/* Narrative Content */}
          <div className="mb-12 lg:mb-0">
            <h2 className="text-blue-600 font-semibold tracking-wide uppercase text-sm mb-3">AI-Core Mission</h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              A Proprietary AI Engine Powering Australian Portfolio Sovereignty
            </h3>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              CanvasEstate is not just a tracking tool—it's a proprietary intelligence platform. Our core technology utilizes advanced AI models specifically trained on the Australian property market to transform fragmented data into predictive wealth-building strategies.
            </p>

            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A2 2 0 013 15.487V6.07a2 2 0 011.242-1.844l1.414-.565a2 2 0 011.58 0l1.414.565A2 2 0 0010 3.75h4a2 2 0 001.35.515l1.414-.565a2 2 0 011.58 0l1.414.565A2 2 0 0121 6.07v9.417a2 2 0 01-1.553 1.944L15 20" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">AI Suburb Selection</h4>
                  <p className="text-gray-600">Leverage multi-factor data analysis to identify high-growth investment hotspots before the market moves. Our AI scans millions of data points to find your next value-add asset.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center text-amber-600">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">Intelligent Lifecycle Tracking</h4>
                  <p className="text-gray-600">Never miss a critical milestone. Automated monitoring of lease expiries, rent reviews, and maintenance compliance, delivered as proactive alerts.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">Automated Tax Documentation</h4>
                  <p className="text-gray-600">Seamlessly extract and organize financial records. Turn invoices and statements into accountant-ready tax packs instantly with neural-network accuracy.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">Unified Portfolio Management</h4>
                  <p className="text-gray-600">Track real-time valuations, rental income, expenses, and loan positions across your entire portfolio. One dashboard to manage all your properties.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center text-cyan-600">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">Professional Collaboration</h4>
                  <p className="text-gray-600">Empower your team with secure, read-only access. Seamlessly share live data with your Buyer Agents for acquisition or your Accountant for seamless tax compliance, all while maintaining full owner sovereignty.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Target Audience Image/Card */}
          <div className="relative">
            <div className="absolute -inset-4 bg-brand-primary/5 rounded-3xl -rotate-2"></div>
            <div className="relative bg-white border border-gray-100 rounded-2xl shadow-xl p-8">
              <h4 className="text-xl font-bold text-brand-secondary mb-4 italic">Built For...</h4>
              <ul className="space-y-4">
                {[
                  { title: 'Portfolio Owners', desc: 'Seeking unified AI oversight for complex holdings.' },
                  { title: 'Aggressive Growth Investors', desc: 'Utilizing data to strategically expand their footprint.' },
                  { title: 'Buyer Agents & Accountants', desc: 'Providing read-only professional support with zero data friction.' },
                  { title: 'Institutional-Grade Landlords', desc: 'Who require strict compliance and automated data hygiene.' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 w-5 h-5 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="block font-bold text-gray-900">{item.title}</span>
                      <span className="text-sm text-gray-500">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessOverview;
